import React, { useState, useEffect } from "react";
import { db, auth } from "./firebase.js";
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, query, where } from "firebase/firestore";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";

const localizer = momentLocalizer(moment);

function CalendarPage({ user, isAdmin, isMobile }) {
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [recurrenceType, setRecurrenceType] = useState("none"); // "none", "weekly", "monthly", "yearly"
  const [occurrences, setOccurrences] = useState("1"); // Number of occurrences
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [editEvent, setEditEvent] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editScope, setEditScope] = useState("this"); // "this" or "all" for editing/deleting recurring events

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        console.log("Fetching events from Firestore...");
        const snapshot = await getDocs(collection(db, "events"));
        console.log("Events snapshot size:", snapshot.size);
        const eventList = snapshot.docs.map(doc => {
          const data = doc.data();
          let start, end;
          if (data.time) {
            const dateTimeString = `${data.date}T${data.time}:00`;
            start = moment(dateTimeString, "YYYY-MM-DDTHH:mm:ss").toDate();
            end = moment(dateTimeString, "YYYY-MM-DDTHH:mm:ss").toDate();
          } else {
            start = moment(data.date, "YYYY-MM-DD").startOf('day').toDate();
            end = moment(data.date, "YYYY-MM-DD").endOf('day').toDate();
          }
          console.log(`Event ${data.title} on ${data.date} at ${data.time || "all day"}: start =`, start, "end =", end);
          return {
            id: doc.id,
            title: data.title,
            date: data.date,
            time: data.time || "",
            recurrenceId: data.recurrenceId || null,
            recurrenceType: data.recurrenceType || "none",
            start: start,
            end: end,
            allDay: !data.time
          };
        });
        console.log("Events list:", eventList);
        setEvents(eventList);
      } catch (error) {
        console.error("Error fetching events:", error);
        setError("Failed to fetch events: " + error.message);
      }
    };
    fetchEvents();
  }, []);

  console.log("CalendarPage rendering with isAdmin:", isAdmin);

  const addEvent = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      const currentUser = auth.currentUser;
      if (currentUser) {
        console.log("Forcing token refresh before write...");
        const token = await currentUser.getIdToken(true);
        const idTokenResult = await currentUser.getIdTokenResult();
        console.log("Refreshed token:", token);
        console.log("Token claims during write:", idTokenResult.claims);
        console.log("isAdmin from props:", isAdmin);
      } else {
        throw new Error("No authenticated user");
      }

      const numOccurrences = parseInt(occurrences, 10);
      if (isNaN(numOccurrences) || numOccurrences < 1) {
        throw new Error("Number of occurrences must be at least 1");
      }

      const baseEvent = {
        title: title,
        date: date,
        time: time || null,
        createdBy: currentUser.uid,
        createdAt: new Date(),
        recurrenceType: recurrenceType,
      };

      const newEvents = [];

      if (recurrenceType !== "none") {
        // Generate recurring events based on type and occurrences
        const recurrenceId = `${title}_${Date.now()}`;
        const startDate = moment(date);
        for (let i = 0; i < numOccurrences; i++) {
          let eventDate;
          if (recurrenceType === "weekly") {
            eventDate = startDate.clone().add(i * 7, 'days').format('YYYY-MM-DD');
          } else if (recurrenceType === "monthly") {
            eventDate = startDate.clone().add(i, 'months').format('YYYY-MM-DD');
          } else if (recurrenceType === "yearly") {
            eventDate = startDate.clone().add(i, 'years').format('YYYY-MM-DD');
          }
          const docRef = await addDoc(collection(db, "events"), {
            ...baseEvent,
            date: eventDate,
            recurrenceId: recurrenceId,
          });
          console.log(`Added event on ${eventDate} with ID: ${docRef.id}`);
          let start, end;
          if (time) {
            const dateTimeString = `${eventDate}T${time}:00`;
            start = moment(dateTimeString, "YYYY-MM-DDTHH:mm:ss").toDate();
            end = moment(dateTimeString, "YYYY-MM-DDTHH:mm:ss").toDate();
          } else {
            start = moment(eventDate, "YYYY-MM-DD").startOf('day').toDate();
            end = moment(eventDate, "YYYY-MM-DD").endOf('day').toDate();
          }
          newEvents.push({
            id: docRef.id,
            title: title,
            date: eventDate,
            time: time || "",
            recurrenceId: recurrenceId,
            recurrenceType: recurrenceType,
            start: start,
            end: end,
            allDay: !time
          });
        }
        console.log(`Recurring ${recurrenceType} events added successfully`);
      } else {
        // Add a single event
        const docRef = await addDoc(collection(db, "events"), {
          ...baseEvent,
          recurrenceId: null,
        });
        console.log(`Added single event on ${date} with ID: ${docRef.id}`);
        let start, end;
        if (time) {
          const dateTimeString = `${date}T${time}:00`;
          start = moment(dateTimeString, "YYYY-MM-DDTHH:mm:ss").toDate();
          end = moment(dateTimeString, "YYYY-MM-DDTHH:mm:ss").toDate();
        } else {
          start = moment(date, "YYYY-MM-DD").startOf('day').toDate();
          end = moment(date, "YYYY-MM-DD").endOf('day').toDate();
        }
        newEvents.push({
          id: docRef.id,
          title: title,
          date: date,
          time: time || "",
          recurrenceId: null,
          recurrenceType: "none",
          start: start,
          end: end,
          allDay: !time
        });
      }

      setTitle("");
      setDate("");
      setTime("");
      setRecurrenceType("none");
      setOccurrences("1");

      // Update the events state optimistically with the new events
      setEvents(prevEvents => [...prevEvents, ...newEvents]);

      // Fetch all events to ensure consistency
      const snapshot = await getDocs(collection(db, "events"));
      console.log("Fetched events after add:", snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })));
      const eventList = snapshot.docs.map(doc => {
        const data = doc.data();
        let start, end;
        if (data.time) {
          const dateTimeString = `${data.date}T${data.time}:00`;
          start = moment(dateTimeString, "YYYY-MM-DDTHH:mm:ss").toDate();
          end = moment(dateTimeString, "YYYY-MM-DDTHH:mm:ss").toDate();
        } else {
          start = moment(data.date, "YYYY-MM-DD").startOf('day').toDate();
          end = moment(data.date, "YYYY-MM-DD").endOf('day').toDate();
        }
        console.log(`Event ${data.title} on ${data.date} at ${data.time || "all day"}: start =`, start, "end =", end);
        return {
          id: doc.id,
          title: data.title,
          date: data.date,
          time: data.time || "",
          recurrenceId: data.recurrenceId || null,
          recurrenceType: data.recurrenceType || "none",
          start: start,
          end: end,
          allDay: !data.time
        };
      });
      setEvents(eventList);
    } catch (error) {
      console.error("Error adding event:", error);
      setError("Failed to add event: " + error.message);
    }
  };

  const editEventData = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      const currentUser = auth.currentUser;
      if (currentUser) {
        console.log("Forcing token refresh before update...");
        const token = await currentUser.getIdToken(true);
        const idTokenResult = await currentUser.getIdTokenResult();
        console.log("Refreshed token:", token);
        console.log("Token claims during update:", idTokenResult.claims);
        console.log("isAdmin from props:", isAdmin);
      } else {
        throw new Error("No authenticated user");
      }

      if (editEvent.recurrenceId && editScope === "all") {
        // Update all events in the series
        const q = query(collection(db, "events"), where("recurrenceId", "==", editEvent.recurrenceId));
        const snapshot = await getDocs(q);
        for (const eventDoc of snapshot.docs) {
          const eventData = eventDoc.data();
          const eventDate = moment(eventData.date);
          const editDate = moment(editEvent.date);
          let newDate;
          if (editEvent.recurrenceType === "weekly") {
            const weekDiff = editDate.diff(eventDate, 'weeks');
            newDate = moment(eventData.date).add(weekDiff * 7, 'days').format('YYYY-MM-DD');
          } else if (editEvent.recurrenceType === "monthly") {
            const monthDiff = editDate.diff(eventDate, 'months');
            newDate = moment(eventData.date).add(monthDiff, 'months').format('YYYY-MM-DD');
          } else if (editEvent.recurrenceType === "yearly") {
            const yearDiff = editDate.diff(eventDate, 'years');
            newDate = moment(eventData.date).add(yearDiff, 'years').format('YYYY-MM-DD');
          }
          await updateDoc(doc(db, "events", eventDoc.id), {
            title: editEvent.title,
            date: newDate,
            time: editEvent.time || null,
            updatedAt: new Date(),
          });
        }
      } else {
        // Update only this event
        const eventRef = doc(db, "events", editEvent.id);
        await updateDoc(eventRef, {
          title: editEvent.title,
          date: editEvent.date,
          time: editEvent.time || null,
          updatedAt: new Date(),
        });
      }

      console.log("Event updated successfully");
      setEditModalOpen(false);
      setEditEvent(null);
      setEditScope("this");

      const snapshot = await getDocs(collection(db, "events"));
      console.log("Fetched events after edit:", snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })));
      const eventList = snapshot.docs.map(doc => {
        const data = doc.data();
        let start, end;
        if (data.time) {
          const dateTimeString = `${data.date}T${data.time}:00`;
          start = moment(dateTimeString, "YYYY-MM-DDTHH:mm:ss").toDate();
          end = moment(dateTimeString, "YYYY-MM-DDTHH:mm:ss").toDate();
        } else {
          start = moment(data.date, "YYYY-MM-DD").startOf('day').toDate();
          end = moment(data.date, "YYYY-MM-DD").endOf('day').toDate();
        }
        console.log(`Event ${data.title} on ${data.date} at ${data.time || "all day"}: start =`, start, "end =", end);
        return {
          id: doc.id,
          title: data.title,
          date: data.date,
          time: data.time || "",
          recurrenceId: data.recurrenceId || null,
          recurrenceType: data.recurrenceType || "none",
          start: start,
          end: end,
          allDay: !data.time
        };
      });
      setEvents(eventList);
    } catch (error) {
      console.error("Error updating event:", error);
      setError("Failed to update event: " + error.message);
    }
  };

  const deleteEvent = async (id, recurrenceId, recurrenceType) => {
    try {
      const currentUser = auth.currentUser;
      if (currentUser) {
        console.log("Forcing token refresh before delete...");
        const token = await currentUser.getIdToken(true);
        const idTokenResult = await currentUser.getIdTokenResult();
        console.log("Refreshed token:", token);
        console.log("Token claims during delete:", idTokenResult.claims);
        console.log("isAdmin from props:", isAdmin);
      } else {
        throw new Error("No authenticated user");
      }

      if (recurrenceId && editScope === "all") {
        // Delete all events in the series
        const q = query(collection(db, "events"), where("recurrenceId", "==", recurrenceId));
        const snapshot = await getDocs(q);
        for (const eventDoc of snapshot.docs) {
          await deleteDoc(doc(db, "events", eventDoc.id));
        }
        setEvents(events.filter(event => event.recurrenceId !== recurrenceId));
      } else {
        // Delete only this event
        await deleteDoc(doc(db, "events", id));
        setEvents(events.filter(event => event.id !== id));
      }

      console.log("Event(s) deleted successfully");
      setEditModalOpen(false);
      setEditEvent(null);
      setEditScope("this");
    } catch (error) {
      console.error("Error deleting event:", error);
      setError("Failed to delete event: " + error.message);
    }
  };

  const openEditModal = (event) => {
    setEditEvent({ ...event });
    setEditModalOpen(true);
    setEditScope("this"); // Default to editing only this event
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditEvent(null);
    setEditScope("this");
    setError(null);
  };

  return (
    <div className="page-container">
      <style>
        {`
          .page-container {
            padding: 2rem;
          }
          .page-title {
            font-size: 1.5rem;
            margin-bottom: 1rem;
          }
          .admin-status {
            margin-bottom: 1rem;
          }
          .error-message {
            color: #ef4444;
            margin-bottom: 1rem;
          }
          .permission-message {
            color: #ef4444;
            margin-bottom: 1rem;
          }
          .add-form {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
            margin-bottom: 1rem;
          }
          .form-input {
            border: 1px solid #d1d5db;
            padding: 0.5rem;
            border-radius: 0.25rem;
            flex: 1;
            min-width: 150px;
            font-size: 1rem;
          }
          .form-select {
            border: 1px solid #d1d5db;
            padding: 0.5rem;
            border-radius: 0.25rem;
            flex: 1;
            min-width: 150px;
            font-size: 1rem;
          }
          .checkbox-group {
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }
          .checkbox-label {
            font-size: 1rem;
            color: #374151;
          }
          .add-button {
            background: #3b82f6;
            color: white;
            padding: 0.5rem 1rem;
            border-radius: 0.25rem;
          }
          .add-button:hover {
            background: #2563eb;
          }
          .calendar-container {
            margin-bottom: 1rem;
            height: 500px;
            opacity: 1; /* Ensure full opacity */
          }
          /* Override react-big-calendar styles */
          .rbc-calendar {
            background: white; /* Solid white background for the calendar */
            opacity: 1 !important; /* Force full opacity */
            border-radius: 0.5rem;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          }
          .rbc-day-bg {
            background: white !important; /* Ensure days without events are opaque */
            opacity: 1 !important;
          }
          .rbc-day-bg.rbc-off-range-bg {
            background: #f3f4f6 !important; /* Slightly different color for off-range days */
            opacity: 1 !important;
          }
          .rbc-event {
            background: #3b82f6 !important; /* Ensure events are visible */
            opacity: 1 !important;
          }
          .event-list-section {
            margin-top: 1rem;
          }
          .event-list-title {
            font-size: 1.25rem;
            margin-bottom: 0.5rem;
          }
          .event-list {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
          }
          .event-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.75rem;
            background: #f3f4f6;
            border-radius: 0.5rem;
            opacity: 1; /* Ensure full opacity */
          }
          .event-details {
            font-size: 1rem;
          }
          .recurring-label {
            font-size: 0.875rem;
            color: #6b7280;
            margin-left: 0.5rem;
          }
          .event-actions {
            display: flex;
            gap: 0.5rem;
          }
          .edit-button {
            background: #eab308;
            color: white;
            padding: 0.25rem 0.75rem;
            border-radius: 0.25rem;
          }
          .edit-button:hover {
            background: #ca8a04;
          }
          .delete-button {
            background: #ef4444;
            color: white;
            padding: 0.25rem 0.75rem;
            border-radius: 0.25rem;
          }
          .delete-button:hover {
            background: #dc2626;
          }
          .no-events {
            color: #4b5563;
          }
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 1000;
          }
          .modal-content {
            background: white;
            padding: 1.5rem;
            border-radius: 0.5rem;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
            max-width: 500px;
            width: 90%;
            z-index: 1001;
          }
          .modal-title {
            font-size: 1.25rem;
            margin-bottom: 1rem;
          }
          .modal-form {
            display: flex;
            flex-direction: column;
            gap: 1rem;
          }
          .form-group {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
          }
          .form-label {
            color: #374151;
          }
          .form-input-modal {
            border: 1px solid #d1d5db;
            padding: 0.5rem;
            border-radius: 0.25rem;
            width: 100%;
            font-size: 1rem;
          }
          .scope-group {
            display: flex;
            gap: 1rem;
            align-items: center;
          }
          .scope-label {
            font-size: 1rem;
            color: #374151;
          }
          .modal-buttons {
            display: flex;
            justify-content: flex-end;
            gap: 0.5rem;
          }
          .cancel-button {
            background: #6b7280;
            color: white;
            padding: 0.5rem 1rem;
            border-radius: 0.25rem;
          }
          .cancel-button:hover {
            background: #4b5563;
          }
          .save-button {
            background: #3b82f6;
            color: white;
            padding: 0.5rem 1rem;
            border-radius: 0.25rem;
          }
          .save-button:hover {
            background: #2563eb;
          }
          @media (max-width: 640px) {
            .page-container {
              padding: 1rem;
            }
            .page-title {
              font-size: 1.25rem;
            }
            .admin-status {
              font-size: 0.875rem;
            }
            .error-message,
            .permission-message {
              font-size: 0.875rem;
            }
            .add-form {
              flex-direction: column;
              gap: 0.5rem;
            }
            .form-input,
            .form-select {
              min-width: 100%;
              font-size: 0.9rem;
              padding: 0.4rem;
            }
            .checkbox-group {
              flex-direction: row;
              align-items: center;
            }
            .checkbox-label {
              font-size: 0.875rem;
            }
            .add-button {
              padding: 0.4rem 0.8rem;
              font-size: 0.9rem;
            }
            .calendar-container {
              height: 400px;
            }
            .event-list-title {
              font-size: 1rem;
            }
            .event-item {
              flex-direction: column;
              align-items: flex-start;
              gap: 0.5rem;
              padding: 0.5rem;
            }
            .event-details {
              font-size: 0.875rem;
            }
            .recurring-label {
              font-size: 0.75rem;
            }
            .event-actions {
              flex-direction: column;
              width: 100%;
            }
            .edit-button,
            .delete-button {
              padding: 0.25rem 0.5rem;
              font-size: 0.875rem;
              width: 100%;
              text-align: center;
            }
            .no-events {
              font-size: 0.875rem;
            }
            .modal-content {
              width: 95%;
              padding: 1rem;
              max-width: 350px;
            }
            .modal-title {
              font-size: 1rem;
            }
            .form-input-modal {
              font-size: 0.9rem;
              padding: 0.4rem;
            }
            .form-label,
            .scope-label {
              font-size: 0.875rem;
            }
            .scope-group {
              flex-direction: column;
              gap: 0.5rem;
            }
            .cancel-button,
            .save-button {
              padding: 0.4rem 0.8rem;
              font-size: 0.9rem;
            }
          }
        `}
      </style>
      <h2 className="page-title">Calendar</h2>
      {error && <p className="error-message">{error}</p>}
      {isAdmin && (
        <form onSubmit={addEvent} className="add-form">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Event Title"
            className="form-input"
            required
          />
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="form-input"
            required
          />
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="form-input"
          />
          <select
            value={recurrenceType}
            onChange={(e) => setRecurrenceType(e.target.value)}
            className="form-select"
          >
            <option value="none">No Recurrence</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
          </select>
          {recurrenceType !== "none" && (
            <input
              type="number"
              value={occurrences}
              onChange={(e) => setOccurrences(e.target.value)}
              placeholder="Number of Occurrences"
              className="form-input"
              min="1"
              required
            />
          )}
          <button type="submit" className="add-button">
            Add Event
          </button>
        </form>
      )}
      <div className="calendar-container">
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: "100%" }}
        />
      </div>
      <div className="event-list-section">
        <h3 className="event-list-title">Event List</h3>
        {events.length > 0 ? (
          <div className="event-list">
            {events.map(event => (
              <div key={event.id} className="event-item">
                <span className="event-details">
                  {event.title} - {event.date} {event.time ? `at ${event.time}` : "(All Day)"}
                  {event.recurrenceId && (
                    <span className="recurring-label">
                      (Recurring {event.recurrenceType.charAt(0).toUpperCase() + event.recurrenceType.slice(1)})
                    </span>
                  )}
                </span>
                {isAdmin && (
                  <div className="event-actions">
                    <button
                      onClick={() => openEditModal(event)}
                      className="edit-button"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteEvent(event.id, event.recurrenceId, event.recurrenceType)}
                      className="delete-button"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="no-events">No events found</p>
        )}
      </div>

      {editModalOpen && (
        <div className="modal-overlay">
          <div className={`modal-content ${isMobile ? 'modal-content-mobile' : ''}`}>
            <h3 className="modal-title">Edit Event</h3>
            {error && <p className="error-message">{error}</p>}
            <form onSubmit={editEventData} className="modal-form">
              <div className="form-group">
                <label className="form-label">Title</label>
                <input
                  type="text"
                  value={editEvent?.title || ""}
                  onChange={(e) => setEditEvent({ ...editEvent, title: e.target.value })}
                  className="form-input-modal"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Date</label>
                <input
                  type="date"
                  value={editEvent?.date || ""}
                  onChange={(e) => setEditEvent({ ...editEvent, date: e.target.value })}
                  className="form-input-modal"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Time (optional)</label>
                <input
                  type="time"
                  value={editEvent?.time || ""}
                  onChange={(e) => setEditEvent({ ...editEvent, time: e.target.value })}
                  className="form-input-modal"
                />
              </div>
              {editEvent?.recurrenceId && (
                <div className="form-group">
                  <label className="form-label">Apply changes to:</label>
                  <div className="scope-group">
                    <label className="scope-label">
                      <input
                        type="radio"
                        value="this"
                        checked={editScope === "this"}
                        onChange={(e) => setEditScope(e.target.value)}
                      />
                      This event only
                    </label>
                    <label className="scope-label">
                      <input
                        type="radio"
                        value="all"
                        checked={editScope === "all"}
                        onChange={(e) => setEditScope(e.target.value)}
                      />
                      All events in the series
                    </label>
                  </div>
                </div>
              )}
              <div className="modal-buttons">
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="cancel-button"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="save-button"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CalendarPage;